import React, { useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  HStack,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'

import axios from 'lib/axios'

import InfoHint from '../components/InfoHint'
import { getErrorMessage } from '../types'

type Props = {
  statementEndDate: string
}

const ReviewAdjustments = ({ statementEndDate }: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [adjustments, setAdjustments] = useState<any[]>([])

  const toCsvCell = (value: any): string => {
    if (value == null) {
      return ''
    }

    if (typeof value === 'object' && 'value' in value) {
      return toCsvCell(value.value)
    }

    const serialized =
      typeof value === 'object' ? JSON.stringify(value) : String(value).replace(/\r?\n/g, ' ')

    return `"${serialized.replace(/"/g, '""')}"`
  }

  const onDownloadCsv = () => {
    if (adjustments.length === 0) {
      return
    }

    const columnNames = Array.from(
      adjustments.reduce<Set<string>>((columns, row) => {
        Object.keys((row || {}) as Record<string, any>).forEach((key) => columns.add(key))
        return columns
      }, new Set<string>())
    )

    const csvRows = [
      columnNames.map((columnName) => toCsvCell(columnName)).join(','),
      ...adjustments.map((row) =>
        columnNames.map((columnName) => toCsvCell(row[columnName])).join(',')
      )
    ]

    const csvBlob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const csvUrl = URL.createObjectURL(csvBlob)
    const anchorElement = document.createElement('a')
    anchorElement.href = csvUrl
    anchorElement.download = `statement_adjustments_${statementEndDate || 'export'}.csv`
    anchorElement.click()
    URL.revokeObjectURL(csvUrl)
  }

  const onLoadAdjustments = async () => {
    try {
      setIsLoading(true)
      setError('')
      const { data } = await axios.get('/admin/statements/adjustments', {
        params: {
          statementEndDate,
          limit: 25
        }
      })
      setAdjustments(data || [])
    } catch (err: any) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Text fontSize='sm' color='gray.700'>
        Review large wallet adjustments so customer-facing month-over-month changes can be explained
        before sending.
      </Text>
      <HStack spacing={3}>
        <Button
          colorScheme='teal'
          variant='outline'
          onClick={onLoadAdjustments}
          isDisabled={isLoading || !statementEndDate}
        >
          Load Top Adjustments
        </Button>
        <InfoHint label='Loads the largest absolute adjustment deltas from staging monthly_customer_statements for the selected statement month (default top 25).' />
        <Button
          colorScheme='teal'
          variant='solid'
          onClick={onDownloadCsv}
          isDisabled={adjustments.length === 0}
        >
          Download CSV (All Columns)
        </Button>
        <InfoHint label='Downloads every returned BigQuery column for deeper QA beyond the abbreviated table preview.' />
        {isLoading && <Spinner size='sm' />}
      </HStack>

      {error && (
        <Alert status='error'>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {adjustments.length > 0 && (
        <TableContainer>
          <Table size='sm'>
            <Thead>
              <Tr>
                <Th>Address</Th>
                <Th>Tenant</Th>
                <Th isNumeric>
                  Adjustments
                  <InfoHint label='Adjustment is the month-over-month discrepancy between prior ending and current starting wallet values. Large values often need explanation.' />
                </Th>
                <Th isNumeric>Starting</Th>
                <Th isNumeric>Ending</Th>
              </Tr>
            </Thead>
            <Tbody>
              {adjustments.map((row, index) => {
                const absAdjustment = Math.abs(Number(row.adjustments || 0))
                return (
                  <Tr key={`${row.rentalId || row.rental_id || index}`}>
                    <Td>{row.address}</Td>
                    <Td>{row.tenant}</Td>
                    <Td isNumeric>
                      {row.adjustments}
                      {absAdjustment > 500 ? ' (Large)' : ''}
                    </Td>
                    <Td isNumeric>{row.starting_balance}</Td>
                    <Td isNumeric>{row.ending_balance}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  )
}

export default ReviewAdjustments
